import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { ArrowLeft, Eye, EyeOff, LayoutList, Clock, ChevronDown, MoreHorizontal } from "lucide-react";

/**
 * Reusable Record Detail Shell (Top-Recruit).
 * Person, Recruiting Opportunity, Agency/Business and future records adopt this shell while
 * preserving their own data models. Provides: record header, Overview/Timeline primary tabs,
 * optional stage tracker, Quick Access + Related Records secondary navigation, Hide Empty Fields.
 */
export function RecordDetailShell({
  backLabel = "Back",
  onBack,
  header,
  stageTracker = null,
  overview,
  timeline = null,
  related = [],
  hideEmpty = false,
  onToggleHideEmpty,
  showHideEmpty = true,
  defaultView = "overview",
  view: controlledView,
  onViewChange,
  quickExtra = [],
  moreItems = [],
}) {
  const [innerView, setInnerView] = useState(defaultView);
  const view = controlledView ?? innerView;
  const setView = (v) => { setInnerView(v); onViewChange?.(v); };
  const [moreOpen, setMoreOpen] = useState(false);
  const showMore = moreOpen || moreItems.some((m) => m.key === view);

  const quickAccess = [
    { key: "overview", label: "Overview", icon: LayoutList },
    ...(timeline ? [{ key: "timeline", label: "Timeline", icon: Clock }] : []),
    ...quickExtra,
  ];

  const NavButton = ({ item }) => {
    const active = view === item.key;
    const Icon = item.icon;
    return (
      <button
        onClick={() => setView(item.key)}
        data-testid={`shell-nav-${item.key}`}
        className={cn(
          "w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap",
          active ? "bg-brand/10 text-brand" : "text-slate-600 hover:bg-slate-100"
        )}
      >
        {Icon && <Icon className="w-4 h-4 shrink-0" />}
        <span className="flex-1 text-left truncate">{item.label}</span>
        {typeof item.count === "number" && (
          <span title={item.countTitle} className={cn("text-[11px] rounded-full px-1.5 py-0.5", active ? "bg-brand/15 text-brand" : "bg-slate-100 text-slate-500")}>
            {item.count}
          </span>
        )}
      </button>
    );
  };

  const activeRelated = [...quickExtra, ...related, ...moreItems].find((r) => r.key === view);

  return (
    <div>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="record-back">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </button>
      )}

      {header}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-6 mt-6">
        {/* Secondary navigation */}
        <aside className="min-w-0 lg:sticky lg:top-20 self-start">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto tr-scroll pb-2 lg:pb-0" data-testid="record-quick-access">
            <p className="hidden lg:block px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Quick Access</p>
            {quickAccess.map((it) => <NavButton key={it.key} item={it} />)}
            {related.length > 0 && (
              <>
                <p className="hidden lg:block px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">Related Records</p>
                {related.map((it) => <NavButton key={it.key} item={it} />)}
              </>
            )}
            {moreItems.length > 0 && (
              <>
                <button onClick={() => setMoreOpen((o) => !o)} aria-expanded={showMore} data-testid="shell-nav-more"
                  className="hidden lg:flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 transition-colors">
                  <MoreHorizontal className="w-4 h-4" /><span className="flex-1 text-left">More</span>
                  <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", showMore && "rotate-180")} />
                </button>
                <div className={cn("contents", !showMore && "lg:hidden")} data-testid="shell-more-items">
                  {moreItems.map((it) => <NavButton key={it.key} item={it} />)}
                </div>
              </>
            )}
          </nav>
        </aside>

        {/* Content */}
        <div className="min-w-0">
          {view === "overview" && (
            <div className="space-y-6">
              {stageTracker}
              {showHideEmpty && onToggleHideEmpty && (
                <div className="flex items-center justify-end">
                  <button
                    onClick={() => onToggleHideEmpty?.(!hideEmpty)}
                    data-testid="hide-empty-toggle"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    {hideEmpty ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {hideEmpty ? "Show Empty Fields" : "Hide Empty Fields"}
                  </button>
                </div>
              )}
              {overview}
            </div>
          )}
          {view === "timeline" && timeline}
          {activeRelated && activeRelated.node}
        </div>
      </div>
    </div>
  );
}
